import { useEffect, useMemo, useSyncExternalStore } from 'react'
import { bg } from 'lib/background'
import { fetchHolds, holdsToSnoozeEntries, saveHold, wakeHold, HOLD_STATES } from './queueHolds'
import { logWarn } from 'lib/logging'

/**
 * Momentum and snooze state for the Action Center.
 *
 * The two halves are scoped differently on purpose, and since TRK-5599 they
 * are held differently too.
 *
 * A snooze belongs to the practice, so it lives on the API now (the Action
 * record, see `queueHolds`). A queue is shared work: if a hygienist sets a
 * patient aside for a week because they have just spoken to them, the front
 * desk must not ring that patient tomorrow. Keying snoozes to the person who
 * set them, or to their browser, would leave the same patient live in one
 * colleague's queue and hidden in another's, and the practice would call them
 * twice. That is worse than losing the state altogether, because it looks
 * like it is working. This module keeps a cache of the workspace's holds,
 * read once per page load, and the writes go through in the background.
 *
 * Momentum is the opposite. "What I have worked today" is one person's tally,
 * stays keyed to them, and stays in the browser.
 */

export type MomentumKind = `call` | `sms` | `task` | `followup` | `status` | `pipeline` | `snooze` | `closed-lost`
export type MomentumEvent = {
  ts: number
  queueId: string
  contactId: string
  kind: MomentumKind
  estValue?: number
}
export type SnoozeEntry = {
  contactId: string
  queueId: string
  until: string
  /** Who set it. Recorded, never keyed on: see the note at the top. */
  byUserId?: string
}

const MOMENTUM_RETENTION_DAYS = 14

const listeners = new Set<() => void>()
let version = 0

function emit() {
  version++
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

const momentumKey = (userId: string) => `action-center:momentum:${userId}`
const snoozeKey = (workspaceId: string) => `action-center:snooze:${workspaceId}`

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return fallback
    return JSON.parse(raw) as T
  } catch (err) {
    logWarn(`action-center`, `Failed to read ${key} from localStorage`, err)
    return fallback
  }
}

function writeJson(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (err) {
    logWarn(`action-center`, `Failed to write ${key} to localStorage`, err)
  }
}

export function recordMomentum(userId: string, event: Omit<MomentumEvent, `ts`>) {
  const cutoff = Date.now() - MOMENTUM_RETENTION_DAYS * 24 * 60 * 60 * 1000
  const events = readJson<MomentumEvent[]>(momentumKey(userId), []).filter((e) => e.ts >= cutoff)
  events.push({ ...event, ts: Date.now() })
  writeJson(momentumKey(userId), events)
  emit()
}

export function todaysEvents(userId: string): MomentumEvent[] {
  const startOfDay = new Date()
  startOfDay.setHours(0, 0, 0, 0)
  return readJson<MomentumEvent[]>(momentumKey(userId), []).filter((e) => e.ts >= startOfDay.getTime())
}

/**
 * The workspace's snoozes, cached from the API.
 *
 * One fetch per page load (per workspace), which is also how a dated snooze
 * expires with no job running anywhere: it simply is not applied once its
 * date has passed. The cache exists because every consumer reads snoozes
 * synchronously mid-render; `emit` is what tells them the fetch landed.
 */
let snoozeCache: SnoozeEntry[] = []
let snoozeCacheWorkspace: string | undefined
let snoozeLoad: Promise<void> | undefined

/**
 * Read the practice's live holds, once.
 *
 * Callers may all ask; only the first fetch per workspace runs. Failure keeps
 * whatever the cache holds and fails open: showing a snoozed card again is
 * recoverable, silently hiding people behind a broken fetch is not.
 */
export function loadSnoozes(workspaceId: string): Promise<void> {
  if (snoozeCacheWorkspace === workspaceId && snoozeLoad) return snoozeLoad
  snoozeCacheWorkspace = workspaceId
  snoozeLoad = fetchHolds()
    .then((holds) => {
      snoozeCache = holdsToSnoozeEntries(holds)
      migrateLegacySnoozes(workspaceId)
      emit()
    })
    .catch((err) => {
      logWarn(`action-center`, `Could not read the workspace's snoozes`, err)
    })
  return snoozeLoad
}

/**
 * One-time promotion of the prototype's localStorage snoozes to the API.
 *
 * Safe because a board snooze is only a date: its meaning does not depend on
 * when the record was written, unlike a response-queue dismissal, which is
 * deliberately not migrated (its wake rule keys on the record's own instant,
 * and rewriting it now would hide rows a newer touch should have revived).
 */
function migrateLegacySnoozes(workspaceId: string) {
  const legacy = readJson<SnoozeEntry[]>(snoozeKey(workspaceId), []).filter((s) => Date.parse(s.until) > Date.now())
  if (!legacy.length) {
    localStorage.removeItem(snoozeKey(workspaceId))
    return
  }
  bg(
    (async () => {
      for (const entry of legacy) {
        await saveHold({
          contactId: entry.contactId,
          queueName: entry.queueId,
          state: HOLD_STATES.snoozed,
          untilDate: entry.until,
        })
      }
      localStorage.removeItem(snoozeKey(workspaceId))
    })(),
  )
  // Applied locally right away; the API catches up in the background.
  const held = new Set(snoozeCache.map((s) => `${s.queueId}:${s.contactId}`))
  snoozeCache = [...snoozeCache, ...legacy.filter((s) => !held.has(`${s.queueId}:${s.contactId}`))]
}

/**
 * Set somebody aside, for the whole practice.
 *
 * `by` is the person doing it, recorded on the entry so the queue can say who
 * set a contact aside and until when. It is not part of the key. The cache
 * moves first so the card leaves at the click; the write follows.
 */
export function snoozeContact(workspaceId: string, contactId: string, queueId: string, until: Date, by?: string) {
  snoozeCache = snoozeCache.filter((s) => !(s.contactId === contactId && s.queueId === queueId))
  snoozeCache.push({ contactId, queueId, until: until.toISOString(), byUserId: by })
  emit()
  bg(saveHold({ contactId, queueName: queueId, state: HOLD_STATES.snoozed, untilDate: until.toISOString() }))
}

/** Active (non-expired) snoozes. Expired entries are simply not returned. */
export function activeSnoozes(workspaceId: string): SnoozeEntry[] {
  if (snoozeCacheWorkspace !== workspaceId) return []
  const now = Date.now()
  return snoozeCache.filter((s) => Date.parse(s.until) > now)
}

/**
 * Wake a contact early, for everybody.
 *
 * Per queue when one is named; every queue's hold on them otherwise. The
 * record is marked woken rather than deleted, so who set it survives.
 */
export function unsnooze(workspaceId: string, contactId: string, queueId?: string) {
  const ending = snoozeCache.filter(
    (s) => s.contactId === contactId && (queueId === undefined || s.queueId === queueId),
  )
  if (!ending.length) return
  snoozeCache = snoozeCache.filter((s) => !ending.includes(s))
  emit()
  bg(
    (async () => {
      for (const entry of ending) await wakeHold(entry.queueId, entry.contactId)
    })(),
  )
}

/** Reactivity signal for callers that read `activeSnoozes` inside a memo. */
export function useSnoozeVersion(): number {
  return useSyncExternalStore(subscribe, () => version)
}

/**
 * Session-scoped preferred snooze duration — the split button remembers how
 * long you like to snooze for, per working session.
 */
const SNOOZE_DAYS_KEY = `action-center:snooze-days`
export const DEFAULT_SNOOZE_DAYS = 7

export function getPreferredSnoozeDays(): number {
  try {
    const value = Number(sessionStorage.getItem(SNOOZE_DAYS_KEY))
    return Number.isFinite(value) && value > 0 ? value : DEFAULT_SNOOZE_DAYS
  } catch (err) {
    logWarn(`action-center`, `Failed to read snooze preference`, err)
    return DEFAULT_SNOOZE_DAYS
  }
}

export function setPreferredSnoozeDays(days: number) {
  try {
    sessionStorage.setItem(SNOOZE_DAYS_KEY, String(days))
  } catch (err) {
    // Session preference only — losing it is harmless.
    logWarn(`action-center`, `Failed to persist snooze preference`, err)
  }
}

export type MomentumSummary = {
  /** Unique contacts with at least one action today. */
  worked: number
  byKind: Partial<Record<MomentumKind, number>>
  /** Sum of est. values across unique contacts actioned today. */
  engagedValue: number
}

export function summarizeMomentum(events: MomentumEvent[]): MomentumSummary {
  const byKind: Partial<Record<MomentumKind, number>> = {}
  const valueByContact = new Map<string, number>()
  for (const event of events) {
    byKind[event.kind] = (byKind[event.kind] ?? 0) + 1
    const prev = valueByContact.get(event.contactId) ?? 0
    valueByContact.set(event.contactId, Math.max(prev, event.estValue ?? 0))
  }
  let engagedValue = 0
  for (const value of valueByContact.values()) engagedValue += value
  return { worked: valueByContact.size, byKind, engagedValue }
}

/**
 * Reactive view of this person's momentum today and the practice's snoozes.
 *
 * Two ids because the two halves have two scopes. Either can be missing while
 * the session loads, and each half falls back on its own.
 */
export function useActionCenterMomentum(userId: string | undefined, workspaceId: string | undefined) {
  const currentVersion = useSyncExternalStore(subscribe, () => version)
  // The snoozes live on the API now. Idempotent: every mount may ask, one
  // fetch per workspace runs, and `emit` re-renders this hook when it lands.
  useEffect(() => {
    if (workspaceId) bg(loadSnoozes(workspaceId))
  }, [workspaceId])
  return useMemo(
    () => ({
      summary: userId
        ? summarizeMomentum(todaysEvents(userId))
        : ({ worked: 0, byKind: {}, engagedValue: 0 } as MomentumSummary),
      snoozes: workspaceId ? activeSnoozes(workspaceId) : ([] as SnoozeEntry[]),
    }),
    // currentVersion is the reactivity signal — recompute whenever the store emits.
    [userId, workspaceId, currentVersion],
  )
}
